// src/screens/OnboardingScreen.js
//
// 3-card onboarding shown once on first launch, and replayable from Info.
// The persisted seen-flag only controls the automatic first-launch display.
//
// NOTE: uses index-based rendering instead of FlatList + scrollToIndex
// because scrollToIndex is unreliable on web (Expo webpack). Swiping left/right
// is still supported via onStartShouldSetResponder + gesture tracking.

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TouchableOpacity,
  SafeAreaView,
  ImageBackground,
  PanResponder,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Radius, Fonts } from '../theme';
import { ONBOARDING_KEY, markOnboardingSeen } from '../utils/onboarding';

const { width } = Dimensions.get('window');

const SLIDES = [
  {
    key: 'sleep',
    icon: 'moon',
    iconColor: '#7c9cff',
    title: 'Layer sounds for perfect sleep',
    body: 'Blend rain, white noise, ocean waves and more. Adjust each volume independently to craft your ideal bedtime soundscape.',
  },
  {
    key: 'timer',
    icon: 'timer-outline',
    iconColor: '#7c9cff',
    title: 'Fall asleep, then silence',
    body: 'Set a sleep timer and SleepMixBlueprint fades everything out automatically — no alarm, no interruptions.',
  },
  {
    key: 'library',
    icon: 'musical-notes',
    iconColor: '#7c9cff',
    title: 'Bring your own music',
    body: 'Import tracks from your device and mix them alongside the built-in sounds. Your library lives in the My Music tab.',
  },
];

export default function OnboardingScreen({ onDone, onClose }) {
  const [activeIndex, setActiveIndex] = useState(0);

  const isLast = activeIndex === SLIDES.length - 1;

  const goTo = (index) => {
    const clamped = Math.max(0, Math.min(SLIDES.length - 1, index));
    setActiveIndex(clamped);
  };

  const handleNext = () => {
    if (isLast) {
      finish();
    } else {
      goTo(activeIndex + 1);
    }
  };

  const finish = async () => {
    // Manual replay is a transient overlay. It must not change the persisted
    // first-launch flag or replace the app tree underneath it.
    if (onClose) {
      onClose();
      return;
    }

    try {
      await markOnboardingSeen();
    } catch (_) {}
    onDone?.();
  };

  // Swipe support — left swipe advances, right swipe goes back
  const panResponder = React.useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) =>
        Math.abs(g.dx) > 10 && Math.abs(g.dx) > Math.abs(g.dy),
      onPanResponderRelease: (_, g) => {
        if (g.dx < -40) {
          setActiveIndex((i) => Math.min(SLIDES.length - 1, i + 1));
        } else if (g.dx > 40) {
          setActiveIndex((i) => Math.max(0, i - 1));
        }
      },
    })
  ).current;

  const slide = SLIDES[activeIndex];

  return (
    <ImageBackground
      source={require('../../assets/sleepmix_logo.png')}
      style={[styles.bg, onClose && styles.replayOverlay]}
      resizeMode="cover"
    >
      {/* Dark overlay — keeps text readable especially on the lighter orange bottom */}
      <View style={styles.overlay} />

      <SafeAreaView style={styles.root}>
        {/* Skip */}
        <TouchableOpacity
          style={styles.skipBtn}
          onPress={finish}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Text style={styles.skipText}>Skip</Text>
        </TouchableOpacity>

        {/* Slide content */}
        <View style={styles.slideArea} {...panResponder.panHandlers}>
          <View style={styles.slide}>
            <View style={styles.iconWrap}>
              <Ionicons name={slide.icon} size={64} color="#fff" />
            </View>
            <Text style={styles.title}>{slide.title}</Text>
            <Text style={styles.body}>{slide.body}</Text>
          </View>
        </View>

        {/* Dots */}
        <View style={styles.dotsRow}>
          {SLIDES.map((s, i) => (
            <TouchableOpacity key={s.key} onPress={() => goTo(i)} hitSlop={8}>
              <View style={[styles.dot, i === activeIndex && styles.dotActive]} />
            </TouchableOpacity>
          ))}
        </View>

        {/* CTA */}
        <TouchableOpacity style={styles.ctaBtn} onPress={handleNext} activeOpacity={0.85}>
          <Text style={styles.ctaText}>{isLast ? 'Get started' : 'Next'}</Text>
        </TouchableOpacity>
      </SafeAreaView>
    </ImageBackground>
  );
}

export { ONBOARDING_KEY };

const styles = StyleSheet.create({
  bg: {
    flex: 1,
  },
  replayOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 100,
    elevation: 100,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  root: {
    flex: 1,
    alignItems: 'center',
  },
  skipBtn: {
    alignSelf: 'flex-end',
    marginTop: 16,
    marginRight: 24,
  },
  skipText: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 15,
    fontFamily: Fonts.medium,
  },

  slideArea: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  slide: {
    width,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingBottom: 40,
  },
  iconWrap: {
    width: 120,
    height: 120,
    borderRadius: Radius.full,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 36,
  },
  title: {
    color: '#ffffff',
    fontSize: 24,
    fontFamily: Fonts.semiBold,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 32,
    textShadowColor: 'rgba(0,0,0,0.6)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  body: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: 16,
    fontFamily: Fonts.regular,
    textAlign: 'center',
    lineHeight: 24,
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },

  dotsRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 28,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.35)',
  },
  dotActive: {
    backgroundColor: '#ffffff',
    width: 24,
    borderRadius: 4,
  },

  ctaBtn: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.7)',
    borderRadius: Radius.pill,
    paddingVertical: 16,
    paddingHorizontal: 48,
    marginBottom: 32,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: Fonts.semiBold,
  },
});
